import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  Icon,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Spinner,
  useDisclosure,
  Badge,
  Flex,
  Tooltip,
  Link,
} from '@chakra-ui/react'
import { FiPaperclip, FiUpload, FiCheck, FiDownload } from 'react-icons/fi'
import { useProdutos } from '../hooks/useProdutos'
import { ColorSelector } from './ColorSelector'
import { useFormContext, Controller } from 'react-hook-form'
import { formatQuantidadeParaExibicao, formatQuantidadeParaCalculos } from '../validationSchema'
import { api } from '../../../../services/api'
import { useDebounce } from '../../../../hooks/useDebounce'

export function ProdutoForm({ subtotal }) {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext()
  const { data: produtos, isLoading, error } = useProdutos()
  const [coresDisponiveis, setCoresDisponiveis] = useState([])
  const [gravacoesDisponiveis, setGravacoesDisponiveis] = useState([])
  const logoInputRef = useRef(null)
  const layoutInputRef = useRef(null)
  const [isCalculatingValues, setIsCalculatingValues] = useState(false)
  const [subtotalValue, setSubtotalValue] = useState('')
  const [hasError, setHasError] = useState(false)
  const [prazoProducao, setPrazoProducao] = useState('10 dias úteis')
  const [isLoadingPrazo, setIsLoadingPrazo] = useState(false)
  const [produtoTemImpressao, setProdutoTemImpressao] = useState(false)
  const [linkBaseLayout, setLinkBaseLayout] = useState(null)

  const requestInProgressRef = useRef(false)

  const produto = watch('produto')
  const logoFile = watch('logoFile')
  const layoutFile = watch('layoutFile')
  const quantidade = watch('quantidade')
  const impressaoId = watch('impressaoId')
  const debouncedQuantidade = useDebounce(quantidade, 500)

  const formatCurrency = (value) => {
    return `R$${value.toFixed(2).replace('.', ',')}`
  }

  const formatCurrencyLarge = (value) => {
    return `R$${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const buscarValorProduto = useCallback(async () => {
    if (!debouncedQuantidade || !impressaoId) return
    if (requestInProgressRef.current) return // Se já está buscando, não inicia nova chamada

    const qtdFormatada = formatQuantidadeParaCalculos(debouncedQuantidade)
    if (qtdFormatada <= 0) return

    // Marca que uma requisição está em andamento
    requestInProgressRef.current = true
    setIsCalculatingValues(true)
    setSubtotalValue('')
    setHasError(false)

    try {
      const response = await api.get('/site/v2/tercerizado/busca-valor-produto-tercerizado', {
        params: {
          quantidade: qtdFormatada,
          id_impressao: impressaoId,
        },
      })

      const { valor_unitario, sub_total } = response.data
      setValue('valorUnitario', valor_unitario, { shouldValidate: true })
      setValue('subTotal', sub_total, { shouldValidate: true })
      setSubtotalValue(formatCurrencyLarge(sub_total))
    } catch (error) {
      console.error('Erro ao buscar valor do produto:', error)
      setHasError(true)
      setValue('valorUnitario', 0, { shouldValidate: true })
      setValue('subTotal', 0, { shouldValidate: true })
    } finally {
      setIsCalculatingValues(false)
      requestInProgressRef.current = false
    }
  }, [debouncedQuantidade, impressaoId, setValue])

  const buscarPrazo = useCallback(async () => {
    if (!debouncedQuantidade) return

    const qtdFormatada = formatQuantidadeParaCalculos(debouncedQuantidade)
    if (qtdFormatada <= 0) return

    setIsLoadingPrazo(true)
    try {
      const response = await api.get('/site/v2/tercerizado/busca-prazo-tercerizado', {
        params: {
          quantidade: qtdFormatada,
          id_impressao: Boolean(produtoTemImpressao) ? impressaoId : null,
        },
      })

      const { prazo } = response.data
      const prazoFormatado = typeof prazo === 'number' ? `${prazo} dias úteis` : prazo
      setPrazoProducao(prazoFormatado)
      setValue('prazoProducao', prazo, { shouldValidate: true })
    } catch (error) {
      console.error('Erro ao buscar prazo:', error)
      setPrazoProducao('10 dias úteis')
    } finally {
      setIsLoadingPrazo(false)
    }
  }, [debouncedQuantidade, setValue, produtoTemImpressao])

  useEffect(() => {
    if (debouncedQuantidade && impressaoId) {
      buscarValorProduto()
      buscarPrazo()
    }
  }, [debouncedQuantidade, impressaoId, buscarValorProduto, buscarPrazo])

  const handleProdutoChange = useCallback(
    (produtoNome) => {
      if (!produtos) return

      const produtoSelecionado = produtos.find((p) => p.nome_produto === produtoNome)

      if (produtoSelecionado) {
        console.log('Produto selecionado:', produtoSelecionado)
        setCoresDisponiveis(produtoSelecionado.cores || [])
        setGravacoesDisponiveis(produtoSelecionado.gravacoes || [])

        setProdutoTemImpressao(produtoSelecionado.impressao === true)

        setLinkBaseLayout(produtoSelecionado.link_base || null)

        setValue('codigoProduto', produtoSelecionado.codigo_produto, { shouldValidate: true })
        setValue('produto', produtoSelecionado.nome_produto, { shouldValidate: true })
        setValue('produtoId', produtoSelecionado.id_item, { shouldValidate: true })

        setValue('gravacao', '', { shouldValidate: true })
        setValue('impressaoId', null, { shouldValidate: true })

        if (produtoSelecionado.cores && produtoSelecionado.cores.length > 0) {
          setValue('cor', produtoSelecionado.cores[0].nome_cor, { shouldValidate: true })
          setValue('corHex', produtoSelecionado.cores[0].hexa_cor, { shouldValidate: true })
        }

        if (produtoSelecionado.gravacoes && produtoSelecionado.gravacoes.length > 0) {
          setValue('gravacao', produtoSelecionado.gravacoes[0].gravacao, { shouldValidate: true })
          setValue('impressaoId', produtoSelecionado.gravacoes[0].id_impressao, { shouldValidate: true })
        }
      }
    },
    [produtos, setValue]
  )

  const handleGravacaoChange = useCallback(
    (gravacaoValue) => {
      if (gravacoesDisponiveis.length > 0) {
        const gravacaoSelecionada = gravacoesDisponiveis.find((g) => g.gravacao === gravacaoValue)
        if (gravacaoSelecionada) {
          setValue('impressaoId', gravacaoSelecionada.id_impressao, { shouldValidate: true })
        }
      }
    },
    [gravacoesDisponiveis, setValue]
  )

  useEffect(() => {
    if (produtos && produtos.length > 0 && (!produto || produto === '')) {
      const produtoInicial = produtos[0]
      handleProdutoChange(produtoInicial.nome_produto)
    }
  }, [produtos, produto, handleProdutoChange])

  const handleColorChange = (field, value) => {
    if (field === 'cor' && coresDisponiveis.length > 0) {
      const corSelecionada = coresDisponiveis.find((c) => c.nome_cor === value)

      setValue('cor', value, { shouldValidate: true })
      setValue('corHex', corSelecionada ? corSelecionada.hexa_cor : '', { shouldValidate: true })
    }
  }

  const handleLogoUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      setValue('logoFile', file, { shouldValidate: true })
    }
  }

  const handleLayoutUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      setValue('layoutFile', file, { shouldValidate: true })
    }
  }

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="8px" overflowX="auto">
      <Table
        variant="striped"
        size="md"
        colorScheme="blackAlpha"
        sx={{
          'tr:nth-of-type(odd) td': { bg: 'white !important' },
          'tr:nth-of-type(even) td': { bg: '#F4F5F5 !important' },
          'th, td': { textAlign: 'center !important' },
        }}
      >
        <Thead bg="#378BBF">
          <Tr>
            <Th fontSize="12px" fontWeight="normal" px={2} py={2} color="white" textAlign="center">
              PRODUTO
            </Th>
            <Th fontSize="12px" fontWeight="normal" px={2} py={2} color="white" textAlign="center">
              COR
            </Th>
            <Th fontSize="12px" fontWeight="normal" px={2} py={2} color="white" textAlign="center">
              GRAVAÇÃO
            </Th>

            <Th fontSize="12px" fontWeight="normal" px={2} py={2} color="white" textAlign="center">
              QUANTIDADE
            </Th>
            <Th fontSize="12px" fontWeight="normal" px={2} py={2} color="white" textAlign="center">
              TIPO
            </Th>
            <Th fontSize="12px" fontWeight="normal" px={2} py={2} color="white" textAlign="center">
              VALOR UNITÁRIO
            </Th>
            <Th fontSize="12px" fontWeight="normal" px={2} py={2} color="white" textAlign="center">
              SUBTOTAL
            </Th>
            <Th fontSize="12px" fontWeight="normal" px={2} py={2} color="white" textAlign="center">
              UPLOAD LOGO
            </Th>
            <Th fontSize="12px" fontWeight="normal" px={2} py={2} color="white" textAlign="center">
              UPLOAD LAYOUT
            </Th>
            <Th fontSize="12px" fontWeight="normal" px={2} py={2} color="white" textAlign="center">
              DOWNLOAD BASE LAYOUT
            </Th>
            <Th fontSize="12px" fontWeight="normal" px={2} py={2} color="white" textAlign="center">
              ESTIMATIVA PRAZO DE PRODUÇÃO
            </Th>
          </Tr>
        </Thead>

        <Tbody>
          <Tr>
            <Td px={2} py={4} textAlign="center">
              <FormControl isInvalid={!!errors.produto || !!errors.produtoId}>
                {isLoading ? (
                  <Spinner size="sm" color="#378BBF" />
                ) : error ? (
                  <Text fontSize="12px" color="red.500">
                    Erro ao carregar produtos
                  </Text>
                ) : (
                  <Controller
                    name="produto"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        onChange={(e) => handleProdutoChange(e.target.value)}
                        border="1px"
                        borderColor={errors.produto || errors.produtoId ? 'red.500' : '#313E44'}
                        borderRadius="8px"
                        maxW="222px"
                        w="full"
                        fontSize="12px"
                        color="#313E44"
                      >
                        <option value="">Selecione um produto</option>
                        {produtos &&
                          produtos.map((produto) => (
                            <option key={produto.id_item} value={produto.nome_produto}>
                              {produto.nome_produto}
                            </option>
                          ))}
                      </Select>
                    )}
                  />
                )}
                <FormErrorMessage fontSize="10px">{errors.produto?.message || errors.produtoId?.message}</FormErrorMessage>
              </FormControl>
            </Td>
            <Td px={2} py={4} textAlign="center">
              <FormControl isInvalid={!!errors.cor}>
                <Controller
                  name="cor"
                  control={control}
                  render={({ field }) => (
                    <ColorSelector
                      value={field.value}
                      onChange={handleColorChange}
                      options={coresDisponiveis}
                      isDisabled={isLoading || coresDisponiveis.length === 0}
                      hasError={!!errors.cor}
                    />
                  )}
                />
                <FormErrorMessage fontSize="10px">{errors.cor?.message}</FormErrorMessage>
              </FormControl>
            </Td>
            <Td px={2} py={4} textAlign="center">
              <FormControl isInvalid={!!errors.gravacao}>
                <Controller
                  name="gravacao"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value)
                        handleGravacaoChange(e.target.value)
                      }}
                      border="1px"
                      borderColor={errors.gravacao ? 'red.500' : '#313E44'}
                      borderRadius="8px"
                      maxW="222px"
                      w="full"
                      fontSize="12px"
                      color="#313E44"
                      placeholder="Selecione uma gravação"
                      isDisabled={gravacoesDisponiveis.length === 0}
                    >
                      {gravacoesDisponiveis.map((item) => (
                        <option key={item.id_impressao} value={item.gravacao}>
                          {item.gravacao}
                        </option>
                      ))}
                    </Select>
                  )}
                />
                <FormErrorMessage fontSize="10px">{errors.gravacao?.message}</FormErrorMessage>
              </FormControl>
            </Td>

            <Td px={2} py={4} textAlign="center">
              <FormControl isInvalid={!!errors.quantidade}>
                <Controller
                  name="quantidade"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/\./g, '').replace(/\D/g, '')
                        const formattedValue = formatQuantidadeParaExibicao(rawValue)
                        field.onChange(formattedValue)
                      }}
                      textAlign="right"
                      border="1px"
                      borderColor={errors.quantidade ? 'red.500' : '#313E44'}
                      borderRadius="8px"
                      maxW="222px"
                      w="full"
                      fontSize="12px"
                      color="#313E44"
                      placeholder="Quantidade"
                    />
                  )}
                />
                <FormErrorMessage fontSize="10px">{errors.quantidade?.message}</FormErrorMessage>
              </FormControl>
            </Td>

            <Td px={2} py={4} textAlign="center">
              <Text fontSize="12px" color="#313E44" fontWeight="medium">
                {produtoTemImpressao ? 'METRO' : 'UNIDADE'}
              </Text>
            </Td>
            <Td px={2} py={4} textAlign="center">
              <Text fontSize="12px" color="#313E44">
                {isCalculatingValues ? (
                  <Text as="span" color="blue.500">
                    Calculando...
                  </Text>
                ) : hasError ? (
                  <Text as="span" color="red.500">
                    Erro ao calcular
                  </Text>
                ) : watch('valorUnitario') ? (
                  formatCurrency(watch('valorUnitario'))
                ) : (
                  ''
                )}
              </Text>
            </Td>
            <Td px={2} py={4} textAlign="center">
              <Text fontSize="12px" color="#313E44" fontWeight="medium">
                {isCalculatingValues ? (
                  <Text as="span" color="blue.500">
                    Calculando...
                  </Text>
                ) : hasError ? (
                  <Text as="span" color="red.500">
                    Erro ao calcular
                  </Text>
                ) : (
                  subtotalValue || ''
                )}
              </Text>
            </Td>
            <Td px={2} py={4} textAlign="center">
              <input type="file" ref={logoInputRef} onChange={handleLogoUpload} style={{ display: 'none' }} accept="image/*" />
              <FormControl isInvalid={!!errors.logoFile}>
                <Flex direction="column" align="center">
                  <Button
                    mx="auto"
                    bg={logoFile ? '#2A6990' : '#378BBF'}
                    color="white"
                    size="sm"
                    w="fit-content"
                    borderRadius="8px"
                    _hover={{ bg: '#2A6990' }}
                    fontSize="12px"
                    onClick={() => logoInputRef.current?.click()}
                    borderColor={errors.logoFile ? 'red.500' : 'transparent'}
                    borderWidth={errors.logoFile ? '1px' : '0'}
                  >
                    {logoFile ? <Icon as={FiCheck} mr="2px" /> : <Icon as={FiPaperclip} />}
                  </Button>
                  {logoFile && (
                    <Tooltip label={logoFile.name} hasArrow placement="top">
                      <Badge fontSize="10px" mt={1} colorScheme="green" maxW="100px" isTruncated>
                        {logoFile.name.length > 15 ? logoFile.name.substring(0, 15) + '...' : logoFile.name}
                      </Badge>
                    </Tooltip>
                  )}
                  {errors.logoFile && (
                    <Text color="red.500" fontSize="10px" mt={1} maxW="150px" textAlign="center">
                      {errors.logoFile.message}
                    </Text>
                  )}
                </Flex>
              </FormControl>
            </Td>
            <Td px={2} py={4} textAlign="center">
              <input type="file" ref={layoutInputRef} onChange={handleLayoutUpload} style={{ display: 'none' }} accept="image/*,.pdf" />
              <FormControl isInvalid={!!errors.layoutFile}>
                <Flex direction="column" align="center">
                  <Button
                    mx="auto"
                    bg={layoutFile ? '#2A6990' : '#378BBF'}
                    color="white"
                    size="sm"
                    w="fit-content"
                    borderRadius="8px"
                    _hover={{ bg: '#2A6990' }}
                    fontSize="12px"
                    onClick={() => layoutInputRef.current?.click()}
                    borderColor={errors.layoutFile ? 'red.500' : 'transparent'}
                    borderWidth={errors.layoutFile ? '1px' : '0'}
                  >
                    {layoutFile ? <Icon as={FiCheck} mr="2px" /> : <Icon as={FiUpload} />}
                  </Button>
                  {layoutFile && (
                    <Tooltip label={layoutFile.name} hasArrow placement="top">
                      <Badge fontSize="10px" mt={1} colorScheme="green" maxW="100px" isTruncated>
                        {layoutFile.name.length > 15 ? layoutFile.name.substring(0, 15) + '...' : layoutFile.name}
                      </Badge>
                    </Tooltip>
                  )}
                  {errors.layoutFile && (
                    <Text color="red.500" fontSize="10px" mt={1} maxW="150px" textAlign="center">
                      {errors.layoutFile.message}
                    </Text>
                  )}
                </Flex>
              </FormControl>
            </Td>
            <Td px={2} py={4} textAlign="center">
              {linkBaseLayout ? (
                <Button
                  mx="auto"
                  bg="#378BBF"
                  color="white"
                  size="sm"
                  w="fit-content"
                  borderRadius="8px"
                  _hover={{ bg: '#2A6990' }}
                  fontSize="12px"
                  as="a"
                  href={linkBaseLayout}
                  download
                  leftIcon={<Icon as={FiDownload} />}
                >
                  Baixar
                </Button>
              ) : (
                <Text fontSize="12px" color="gray.500">
                  Não disponível
                </Text>
              )}
            </Td>
            <Td px={2} py={4} textAlign="center">
              <Text fontSize="12px" color="#313E44">
                {isLoadingPrazo ? (
                  <Text as="span" color="blue.500">
                    Calculando...
                  </Text>
                ) : (
                  prazoProducao
                )}
              </Text>
            </Td>
          </Tr>
        </Tbody>

        <Tbody>
          <Tr>
            <Td colSpan="11" px={2} py={4}>
              <Text fontSize="12px" color="gray.600" fontStyle="italic">
                * O prazo de produção é contado após o recebimento do material
              </Text>
            </Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>
  )
}
